import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Percent, Calculator, TrendingUp, TrendingDown, Copy, RotateCcw } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import toast from 'react-hot-toast';

interface CalculationResult {
  type: string;
  description: string;
  formula: string;
  result: number;
  formatted: string;
}

export const PercentageCalculatorTool: React.FC = () => {
  const { t } = useTranslation();
  
  // Basic percentage calculations
  const [value, setValue] = useState<string>('');
  const [percentage, setPercentage] = useState<string>('');
  const [basicResult, setBasicResult] = useState<CalculationResult | null>(null);
  
  // Percentage change calculations
  const [oldValue, setOldValue] = useState<string>('');
  const [newValue, setNewValue] = useState<string>('');
  const [changeResult, setChangeResult] = useState<CalculationResult | null>(null);
  
  // Percentage of total calculations
  const [partValue, setPartValue] = useState<string>('');
  const [totalValue, setTotalValue] = useState<string>('');
  const [totalResult, setTotalResult] = useState<CalculationResult | null>(null);
  
  // Discount/markup calculations
  const [originalPrice, setOriginalPrice] = useState<string>('');
  const [discountPercent, setDiscountPercent] = useState<string>('');
  const [discountResult, setDiscountResult] = useState<CalculationResult[]>([]);
  
  // Tip calculator
  const [billAmount, setBillAmount] = useState<string>('');
  const [tipPercent, setTipPercent] = useState<string>('15');
  const [numberOfPeople, setNumberOfPeople] = useState<string>('1');
  const [tipResult, setTipResult] = useState<CalculationResult[]>([]);

  const formatNumber = (num: number): string => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(num);
  };

  const formatCurrency = (num: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(num);
  };

  // Calculate basic percentage (X% of Y)
  const calculateBasicPercentage = () => {
    const val = parseFloat(value);
    const perc = parseFloat(percentage);
    
    if (isNaN(val) || isNaN(perc)) {
      setBasicResult(null);
      return;
    }
    
    const result = (perc / 100) * val;
    setBasicResult({
      type: 'basic',
      description: `${perc}% of ${formatNumber(val)}`,
      formula: `(${perc} ÷ 100) × ${formatNumber(val)}`,
      result,
      formatted: formatNumber(result)
    });
  };

  // Calculate percentage change
  const calculatePercentageChange = () => {
    const oldVal = parseFloat(oldValue);
    const newVal = parseFloat(newValue);
    
    if (isNaN(oldVal) || isNaN(newVal) || oldVal === 0) {
      setChangeResult(null);
      return;
    }
    
    const change = ((newVal - oldVal) / oldVal) * 100;
    const isIncrease = change > 0;
    
    setChangeResult({
      type: 'change',
      description: `${isIncrease ? t('tools.percentageCalculator.increase') : t('tools.percentageCalculator.decrease')} from ${formatNumber(oldVal)} to ${formatNumber(newVal)}`,
      formula: `((${formatNumber(newVal)} - ${formatNumber(oldVal)}) ÷ ${formatNumber(oldVal)}) × 100`,
      result: Math.abs(change),
      formatted: `${isIncrease ? '+' : '-'}${formatNumber(Math.abs(change))}%`
    });
  };

  // Calculate percentage of total
  const calculatePercentageOfTotal = () => {
    const part = parseFloat(partValue);
    const total = parseFloat(totalValue);
    
    if (isNaN(part) || isNaN(total) || total === 0) {
      setTotalResult(null);
      return;
    }
    
    const percentage = (part / total) * 100;
    setTotalResult({
      type: 'total',
      description: `${formatNumber(part)} is what percentage of ${formatNumber(total)}`,
      formula: `(${formatNumber(part)} ÷ ${formatNumber(total)}) × 100`,
      result: percentage,
      formatted: `${formatNumber(percentage)}%`
    });
  };

  // Calculate discount/markup
  const calculateDiscount = () => {
    const price = parseFloat(originalPrice);
    const discount = parseFloat(discountPercent);
    
    if (isNaN(price) || isNaN(discount)) {
      setDiscountResult([]);
      return;
    }
    
    const discountAmount = (discount / 100) * price;
    const finalPrice = price - discountAmount;
    const savings = discountAmount;
    
    setDiscountResult([
      {
        type: 'discount_amount',
        description: t('tools.percentageCalculator.discountAmount'),
        formula: `${formatNumber(discount)}% × ${formatCurrency(price)}`,
        result: discountAmount,
        formatted: formatCurrency(discountAmount)
      },
      {
        type: 'final_price',
        description: t('tools.percentageCalculator.finalPrice'),
        formula: `${formatCurrency(price)} - ${formatCurrency(discountAmount)}`,
        result: finalPrice,
        formatted: formatCurrency(finalPrice)
      },
      {
        type: 'savings',
        description: t('tools.percentageCalculator.youSave'),
        formula: `${formatNumber(discount)}% discount`,
        result: savings,
        formatted: formatCurrency(savings)
      }
    ]);
  };

  // Calculate tip
  const calculateTip = () => {
    const bill = parseFloat(billAmount);
    const tip = parseFloat(tipPercent);
    const people = parseInt(numberOfPeople);
    
    if (isNaN(bill) || isNaN(tip) || isNaN(people) || people === 0) {
      setTipResult([]);
      return;
    }
    
    const tipAmount = (tip / 100) * bill;
    const totalAmount = bill + tipAmount;
    const perPersonBill = bill / people;
    const perPersonTip = tipAmount / people;
    const perPersonTotal = totalAmount / people;
    
    setTipResult([
      {
        type: 'tip_amount',
        description: t('tools.percentageCalculator.tipAmount'),
        formula: `${formatNumber(tip)}% × ${formatCurrency(bill)}`,
        result: tipAmount,
        formatted: formatCurrency(tipAmount)
      },
      {
        type: 'total_amount',
        description: t('tools.percentageCalculator.totalAmount'),
        formula: `${formatCurrency(bill)} + ${formatCurrency(tipAmount)}`,
        result: totalAmount,
        formatted: formatCurrency(totalAmount)
      },
      ...(people > 1 ? [
        {
          type: 'per_person_bill',
          description: t('tools.percentageCalculator.billPerPerson'),
          formula: `${formatCurrency(bill)} ÷ ${people}`,
          result: perPersonBill,
          formatted: formatCurrency(perPersonBill)
        },
        {
          type: 'per_person_tip',
          description: t('tools.percentageCalculator.tipPerPerson'),
          formula: `${formatCurrency(tipAmount)} ÷ ${people}`,
          result: perPersonTip,
          formatted: formatCurrency(perPersonTip)
        },
        {
          type: 'per_person_total',
          description: t('tools.percentageCalculator.totalPerPerson'),
          formula: `${formatCurrency(totalAmount)} ÷ ${people}`,
          result: perPersonTotal,
          formatted: formatCurrency(perPersonTotal)
        }
      ] : [])
    ]);
  };

  // Auto-calculate when inputs change
  useEffect(() => {
    calculateBasicPercentage();
  }, [value, percentage]);

  useEffect(() => {
    calculatePercentageChange();
  }, [oldValue, newValue]);

  useEffect(() => {
    calculatePercentageOfTotal();
  }, [partValue, totalValue]);

  useEffect(() => {
    calculateDiscount();
  }, [originalPrice, discountPercent]);

  useEffect(() => {
    calculateTip();
  }, [billAmount, tipPercent, numberOfPeople]);

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('common.copied'));
    } catch (err) {
      toast.error(t('common.failedToCopy'));
    }
  };

  const clearAll = () => {
    setValue('');
    setPercentage('');
    setOldValue('');
    setNewValue('');
    setPartValue('');
    setTotalValue('');
    setOriginalPrice('');
    setDiscountPercent('');
    setBillAmount('');
    setTipPercent('15');
    setNumberOfPeople('1');
    setBasicResult(null);
    setChangeResult(null);
    setTotalResult(null);
    setDiscountResult([]);
    setTipResult([]);
  };

  const ResultCard: React.FC<{ result: CalculationResult; showCopy?: boolean }> = ({ result, showCopy = true }) => (
    <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800">
      <div className="flex items-center justify-between mb-2">
        <h4 className="font-medium text-gray-900 dark:text-gray-100">{result.description}</h4>
        {showCopy && (
          <Button
            onClick={() => copyToClipboard(result.formatted)}
            variant="outline"
            size="sm"
          >
            <Copy className="w-3 h-3" />
          </Button>
        )}
      </div>
      <div className="text-2xl font-bold text-blue-600 dark:text-blue-400 mb-1">
        {result.formatted}
      </div>
      <div className="text-sm text-gray-600 dark:text-gray-400 font-mono">
        {result.formula}
      </div>
    </div>
  );

  const commonTipPercentages = [10, 15, 18, 20, 25];

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Percent className="w-5 h-5" />
              <span>{t('tools.percentageCalculator.name')}</span>
            </div>
            <Button onClick={clearAll} variant="outline" size="sm">
              <RotateCcw className="w-4 h-4 mr-2" />
              {t('tools.percentageCalculator.clearAll')}
            </Button>
          </CardTitle>
        </CardHeader>
      </Card>

      {/* Basic Percentage Calculator */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Calculator className="w-5 h-5" />
            <span>{t('tools.percentageCalculator.basicPercentage')}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <Input
              label={t('tools.percentageCalculator.whatIs')}
              value={percentage}
              onChange={(e) => setPercentage(e.target.value)}
              placeholder="25"
              type="number"
            />
            <Input
              label={t('tools.percentageCalculator.percentOf')}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="200"
              type="number"
            />
          </div>
          {basicResult && <ResultCard result={basicResult} />}
        </CardContent>
      </Card>

      {/* Percentage Change Calculator */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <TrendingUp className="w-5 h-5" />
            <span>{t('tools.percentageCalculator.percentageChange')}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <Input
              label={t('tools.percentageCalculator.originalValue')}
              value={oldValue}
              onChange={(e) => setOldValue(e.target.value)}
              placeholder="100"
              type="number"
            />
            <Input
              label={t('tools.percentageCalculator.newValue')}
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
              placeholder="120"
              type="number"
            />
          </div>
          {changeResult && (
            <div className="flex items-center space-x-2">
              {changeResult.formatted.startsWith('+') ? (
                <TrendingUp className="w-5 h-5 text-green-600" />
              ) : (
                <TrendingDown className="w-5 h-5 text-red-600" />
              )}
              <ResultCard result={changeResult} />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Percentage of Total Calculator */}
      <Card>
        <CardHeader>
          <CardTitle>{t('tools.percentageCalculator.percentageOfTotal')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <Input
              label={t('tools.percentageCalculator.partValue')}
              value={partValue}
              onChange={(e) => setPartValue(e.target.value)}
              placeholder="25"
              type="number"
            />
            <Input
              label={t('tools.percentageCalculator.totalValue')}
              value={totalValue}
              onChange={(e) => setTotalValue(e.target.value)}
              placeholder="100"
              type="number"
            />
          </div>
          {totalResult && <ResultCard result={totalResult} />}
        </CardContent>
      </Card>

      {/* Discount Calculator */}
      <Card>
        <CardHeader>
          <CardTitle>{t('tools.percentageCalculator.discountCalculator')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <Input
              label={t('tools.percentageCalculator.originalPrice')}
              value={originalPrice}
              onChange={(e) => setOriginalPrice(e.target.value)}
              placeholder="100"
              type="number"
            />
            <Input
              label={t('tools.percentageCalculator.discountPercent')}
              value={discountPercent}
              onChange={(e) => setDiscountPercent(e.target.value)}
              placeholder="20"
              type="number"
            />
          </div>
          {discountResult.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {discountResult.map((result, index) => (
                <ResultCard key={index} result={result} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tip Calculator */}
      <Card>
        <CardHeader>
          <CardTitle>{t('tools.percentageCalculator.tipCalculator')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <Input
              label={t('tools.percentageCalculator.billAmount')}
              value={billAmount}
              onChange={(e) => setBillAmount(e.target.value)}
              placeholder="50"
              type="number"
            />
            <div>
              <Input
                label={t('tools.percentageCalculator.tipPercent')}
                value={tipPercent}
                onChange={(e) => setTipPercent(e.target.value)}
                placeholder="15"
                type="number"
              />
              <div className="flex flex-wrap gap-1 mt-2">
                {commonTipPercentages.map((tip) => (
                  <Button
                    key={tip}
                    onClick={() => setTipPercent(String(tip))}
                    variant={tipPercent === String(tip) ? 'primary' : 'outline'}
                    size="sm"
                  >
                    {tip}%
                  </Button>
                ))}
              </div>
            </div>
            <Input
              label={t('tools.percentageCalculator.numberOfPeople')}
              value={numberOfPeople}
              onChange={(e) => setNumberOfPeople(e.target.value)}
              placeholder="1"
              type="number"
              min="1"
            />
          </div>
          {tipResult.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {tipResult.map((result, index) => (
                <ResultCard key={index} result={result} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Reference */}
      <Card>
        <CardHeader>
          <CardTitle>Percentage Quick Reference</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">Common Percentages</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>10%</span>
                  <span className="font-mono">0.10</span>
                </div>
                <div className="flex justify-between">
                  <span>25%</span>
                  <span className="font-mono">0.25 (1/4)</span>
                </div>
                <div className="flex justify-between">
                  <span>50%</span>
                  <span className="font-mono">0.50 (1/2)</span>
                </div>
                <div className="flex justify-between">
                  <span>75%</span>
                  <span className="font-mono">0.75 (3/4)</span>
                </div>
                <div className="flex justify-between">
                  <span>100%</span>
                  <span className="font-mono">1.00</span>
                </div>
              </div>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">Formulas</h4>
              <div className="space-y-2 text-sm">
                <div>
                  <strong>Percentage:</strong>
                  <div className="font-mono text-gray-600 dark:text-gray-400">(Part ÷ Whole) × 100</div>
                </div>
                <div>
                  <strong>Percentage Change:</strong>
                  <div className="font-mono text-gray-600 dark:text-gray-400">((New - Old) ÷ Old) × 100</div>
                </div>
                <div>
                  <strong>Percentage of Value:</strong>
                  <div className="font-mono text-gray-600 dark:text-gray-400">(Percentage ÷ 100) × Value</div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
